import { Box, Typography, useTheme } from '@mui/material';
import { useId, useRef, type KeyboardEvent } from 'react';
import { ONBOARDING_STEP_TRAINING_PACKAGE_FIGMA } from '../OnboardingWizard/onboardingFigmaSpec';
import { FORM_FIELD_SPEC, resolveAtlasLensColors } from './formFieldSpec';
import { TextWithAccessiblePlus } from './TextWithAccessiblePlus';

function TrainingPackageRadioVisual({
  checked,
  size,
  indicatorSize,
  typeDefault,
  surfaceDefault,
}: {
  checked: boolean;
  size: number;
  indicatorSize: number;
  typeDefault: string;
  surfaceDefault: string;
}) {
  if (checked) {
    return (
      <Box
        aria-hidden
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: typeDefault,
          border: `1px solid ${typeDefault}`,
          boxSizing: 'border-box',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          '&::after': {
            content: '""',
            width: indicatorSize,
            height: indicatorSize,
            borderRadius: '50%',
            backgroundColor: surfaceDefault,
          },
        }}
      />
    );
  }

  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: surfaceDefault,
        border: `1px solid ${typeDefault}`,
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    />
  );
}

export interface TrainingPackageRadioOption {
  value: string;
  title: string;
  description: string;
}

export interface FormTrainingPackageRadioGroupProps {
  name: string;
  value: string;
  options: ReadonlyArray<TrainingPackageRadioOption>;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

/** Figma 16939:3708 / 16940:6280 — radio list cards with sky-blue selected state. */
export function FormTrainingPackageRadioGroup({
  name,
  value,
  options,
  onChange,
  error,
  disabled = false,
  'aria-label': ariaLabel,
}: FormTrainingPackageRadioGroupProps) {
  const groupId = useId();
  const optionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const theme = useTheme();
  const { optionCard } = ONBOARDING_STEP_TRAINING_PACKAGE_FIGMA;
  const { typography } = FORM_FIELD_SPEC;

  const {
    typeDefault,
    typeMuted,
    typeDisabled,
    surface,
    surfaceSubtle,
    cardBorder,
    cardBackground,
    cardSelectedBackground,
    cardSelectedHover,
    focusRing,
    statusErrorText: errorColor,
  } = resolveAtlasLensColors(theme);

  const titleColor = disabled ? typeDisabled : typeDefault;
  const descriptionColor = disabled ? typeDisabled : typeMuted;

  const titleSx = {
    fontSize: typography.bodyEmphasis.fontSize,
    lineHeight: `${typography.bodyEmphasis.lineHeight}px`,
    letterSpacing: typography.bodyEmphasis.letterSpacing,
    fontWeight: typography.bodyEmphasis.fontWeight,
    color: titleColor,
  };

  const descriptionSx = {
    fontSize: typography.textSm.fontSize,
    lineHeight: `${typography.textSm.lineHeight}px`,
    letterSpacing: typography.textSm.letterSpacing,
    fontWeight: typography.textSm.fontWeight,
    color: descriptionColor,
  };

  const focusOption = (optionValue: string) => {
    optionRefs.current[optionValue]?.focus({ preventScroll: true });
  };

  const handleOptionKeyDown = (optionValue: string, event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    const optionIndex = options.findIndex((option) => option.value === optionValue);

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      onChange(optionValue);
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      const nextIndex = (optionIndex + 1) % options.length;
      const nextValue = options[nextIndex].value;
      onChange(nextValue);
      focusOption(nextValue);
      return;
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const nextIndex = optionIndex <= 0 ? options.length - 1 : optionIndex - 1;
      const nextValue = options[nextIndex].value;
      onChange(nextValue);
      focusOption(nextValue);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        role="radiogroup"
        aria-label={ariaLabel}
        aria-required
        aria-disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${groupId}-error` : undefined}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${optionCard.listGap}px`,
          width: '100%',
        }}
      >
        {options.map((option) => {
          const selected = value === option.value;
          const optionId = `${groupId}-${option.value}`;
          const isTabStop =
            selected || (value === '' && option.value === options[0]?.value);

          return (
            <Box
              key={option.value}
              id={optionId}
              ref={(node: HTMLDivElement | null) => {
                optionRefs.current[option.value] = node;
              }}
              role="radio"
              aria-checked={selected}
              aria-disabled={disabled}
              tabIndex={disabled ? -1 : isTabStop ? 0 : -1}
              onMouseDown={(event) => {
                if (disabled) {
                  return;
                }
                // Avoid focus-scroll jitter when clicking cards (especially the last one).
                event.preventDefault();
              }}
              onClick={() => {
                if (!disabled) {
                  onChange(option.value);
                }
              }}
              onKeyDown={(event) => handleOptionKeyDown(option.value, event)}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: `${optionCard.radioGap}px`,
                boxSizing: 'border-box',
                width: '100%',
                minHeight: optionCard.minHeight,
                p: `${optionCard.paddingY}px ${optionCard.paddingX}px`,
                cursor: disabled ? 'not-allowed' : 'pointer',
                borderWidth: optionCard.radioBorderWidth,
                borderStyle: 'solid',
                borderColor: cardBorder,
                borderRadius: `${optionCard.borderRadius}px`,
                backgroundColor: disabled
                  ? selected
                    ? surfaceSubtle
                    : cardBackground
                  : selected
                    ? cardSelectedBackground
                    : cardBackground,
                outline: 'none',
                '&:hover': disabled
                  ? undefined
                  : {
                      backgroundColor: selected ? cardSelectedHover : cardBackground,
                    },
                '&:focus-visible': disabled
                  ? undefined
                  : {
                      boxShadow: focusRing,
                    },
              }}
            >
              <TrainingPackageRadioVisual
                checked={selected}
                size={optionCard.radioSize}
                indicatorSize={optionCard.radioIndicatorSize}
                typeDefault={disabled ? typeDisabled : typeDefault}
                surfaceDefault={surface}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: `${optionCard.labelGap}px`,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography component="span" sx={titleSx}>
                  {option.title}
                </Typography>
                <Typography component="span" sx={descriptionSx}>
                  <TextWithAccessiblePlus text={option.description} />
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
      {error && (
        <Typography
          id={`${groupId}-error`}
          role="alert"
          sx={{
            mt: 1,
            mx: 0,
            fontSize: typography.textSm.fontSize,
            lineHeight: `${typography.textSm.lineHeight}px`,
            color: errorColor,
          }}
        >
          {error}
        </Typography>
      )}
      {/* Hidden field for form semantics — not focusable. */}
      <Box
        component="input"
        type="hidden"
        name={name}
        value={value}
        readOnly
        tabIndex={-1}
        aria-hidden
      />
    </Box>
  );
}
